/**
 * Blueprint forecast snapshot loader.
 *
 * Forecasts are published as static Pages assets. The browser validates the
 * manifest and snapshot before using them, then caches the last valid copy
 * locally so a missed refresh does not interrupt an active draft.
 */

import type { BlueprintSnapshot } from '../core/blueprint.ts';
import { BLUEPRINT_SCHEMA_VERSION } from '../core/blueprint.ts';
import { idbGet, idbSet } from '../state/storage.ts';

export type BlueprintLoadStatus = 'ready' | 'disabled';

export interface BlueprintManifest {
  schema_version: number;
  model_version: string;
  season: string;
  generated_at: string;
  expires_at: string;
  artifact_url: string;
  artifact_sha256?: string;
  source: BlueprintSnapshot['source'];
  training_seasons: number[];
  data_cutoffs: Record<string, string>;
  attribution: string[];
}

export interface BlueprintLoadResult {
  status: BlueprintLoadStatus;
  snapshot: BlueprintSnapshot | null;
  manifest: BlueprintManifest | null;
  source: 'network' | 'cache' | 'none';
  message: string | null;
}

const MANIFEST_URL = '/blueprint/manifest.json';
const CACHE_KEY = 'blueprint_snapshot_latest';
const CACHE_TTL_MS = 72 * 60 * 60 * 1000;

export async function loadBlueprintSnapshot(
  fetcher: typeof fetch = fetch,
  manifestUrl = MANIFEST_URL,
): Promise<BlueprintLoadResult> {
  try {
    const manifestResponse = await fetcher(manifestUrl, { cache: 'no-store' });
    if (!manifestResponse.ok) throw new Error(`Blueprint manifest returned HTTP ${manifestResponse.status}.`);
    const manifest = validateManifest(await manifestResponse.json());
    if (Date.parse(manifest.expires_at) <= Date.now()) throw new Error('The latest Blueprint forecast has expired.');

    const artifactResponse = await fetcher(new URL(manifest.artifact_url, new URL(manifestUrl, locationOrigin())).toString(), {
      cache: 'no-store',
    });
    if (!artifactResponse.ok) throw new Error(`Blueprint forecast returned HTTP ${artifactResponse.status}.`);
    const artifactText = await artifactResponse.text();
    if (manifest.artifact_sha256 && (await sha256(artifactText)) !== manifest.artifact_sha256) {
      throw new Error('Blueprint forecast checksum does not match its manifest.');
    }
    const snapshot = validateSnapshot(JSON.parse(artifactText), manifest);
    await idbSet(CACHE_KEY, snapshot, CACHE_TTL_MS);
    return { status: 'ready', snapshot, manifest, source: 'network', message: null };
  } catch (error) {
    const cached = await idbGet<BlueprintSnapshot>(CACHE_KEY);
    if (cached && isUsable(cached)) {
      return {
        status: 'ready',
        snapshot: cached,
        manifest: manifestFromSnapshot(cached),
        source: 'cache',
        message: 'Using the last valid Blueprint forecast while the latest snapshot is unavailable.',
      };
    }
    return disabled(error instanceof Error ? error.message : 'Blueprint forecast is unavailable.');
  }
}

export function validateManifest(value: unknown): BlueprintManifest {
  if (!isRecord(value)) throw new Error('Blueprint manifest is not an object.');
  const required = ['model_version', 'season', 'generated_at', 'expires_at', 'artifact_url'];
  for (const key of required) {
    if (typeof value[key] !== 'string' || value[key] === '') throw new Error(`Blueprint manifest is missing ${key}.`);
  }
  if (value.schema_version !== BLUEPRINT_SCHEMA_VERSION) throw new Error('Blueprint manifest schema is unsupported.');
  if (!Array.isArray(value.training_seasons) || !value.training_seasons.every((year) => Number.isInteger(year))) {
    throw new Error('Blueprint manifest training seasons are invalid.');
  }
  return {
    schema_version: BLUEPRINT_SCHEMA_VERSION,
    model_version: String(value.model_version),
    season: String(value.season),
    generated_at: String(value.generated_at),
    expires_at: String(value.expires_at),
    artifact_url: String(value.artifact_url),
    artifact_sha256: typeof value.artifact_sha256 === 'string' ? value.artifact_sha256 : undefined,
    source: value.source === 'curve-baseline' ? 'curve-baseline' : 'open-model',
    training_seasons: value.training_seasons as number[],
    data_cutoffs: isRecord(value.data_cutoffs) ? mapStrings(value.data_cutoffs) : {},
    attribution: Array.isArray(value.attribution) ? value.attribution.filter((x): x is string => typeof x === 'string') : [],
  };
}

export function validateSnapshot(value: unknown, manifest?: BlueprintManifest): BlueprintSnapshot {
  if (!isRecord(value) || value.schema_version !== BLUEPRINT_SCHEMA_VERSION || !isRecord(value.players)) {
    throw new Error('Blueprint forecast schema is unsupported.');
  }
  const snapshot = value as unknown as BlueprintSnapshot;
  if (!snapshot.model_version || !snapshot.generated_at || !snapshot.expires_at) {
    throw new Error('Blueprint forecast metadata is incomplete.');
  }
  if (!isUsable(snapshot)) throw new Error('Blueprint forecast has expired.');
  for (const [id, player] of Object.entries(snapshot.players)) {
    if (!isRecord(player) || player.player_key !== id || !validForecastRow(player)) {
      throw new Error(`Blueprint forecast row ${id} is invalid.`);
    }
  }
  if (manifest && snapshot.model_version !== manifest.model_version) {
    throw new Error('Blueprint manifest and forecast versions do not match.');
  }
  return snapshot;
}

function isUsable(snapshot: BlueprintSnapshot): boolean {
  return snapshot.schema_version === BLUEPRINT_SCHEMA_VERSION && Date.parse(snapshot.expires_at) > Date.now();
}

function validForecastRow(player: Record<string, any>): boolean {
  const positions = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];
  const numeric = ['mean_points', 'p10_points', 'p50_points', 'p90_points', 'expected_games_active', 'breakout_probability', 'uncertainty'];
  return positions.includes(String(player.position)) &&
    numeric.every((key) => typeof player[key] === 'number' && Number.isFinite(player[key])) &&
    player.p10_points <= player.p50_points &&
    player.p50_points <= player.p90_points &&
    player.expected_games_active >= 0 && player.expected_games_active <= 18 &&
    player.breakout_probability >= 0 && player.breakout_probability <= 1;
}

async function sha256(value: string): Promise<string> {
  if (typeof crypto === 'undefined' || !crypto.subtle) throw new Error('Blueprint checksum validation is unavailable in this browser.');
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function manifestFromSnapshot(snapshot: BlueprintSnapshot): BlueprintManifest {
  return {
    schema_version: snapshot.schema_version,
    model_version: snapshot.model_version,
    season: snapshot.season,
    generated_at: snapshot.generated_at,
    expires_at: snapshot.expires_at,
    artifact_url: '',
    source: snapshot.source,
    training_seasons: snapshot.training_seasons,
    data_cutoffs: snapshot.data_cutoffs,
    attribution: snapshot.attribution,
  };
}

function disabled(message: string): BlueprintLoadResult {
  return { status: 'disabled', snapshot: null, manifest: null, source: 'none', message };
}

function isRecord(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function mapStrings(value: Record<string, any>): Record<string, string> {
  return Object.fromEntries(Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === 'string'));
}

function locationOrigin(): string {
  return typeof location === 'undefined' ? 'http://localhost/' : location.origin + '/';
}
